import { type RootState } from '@/store';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { HOMEOWNERS_INSURANCE_RATE } from '@/utils/calculator/assumptions';
import { estimateStateClosingTaxes } from '@/utils/fees/stateClosingTaxes';
import {
  CalculateClosingDisclosureProps,
  calculateTaxFactor,
  calculateTransferTaxFactor,
  chooseMarketInterestRate,
} from '.';
import { calculateEnhancedInterestRate } from './rateIntegration';

// Enhanced interest rate calculation for Commercial loans based on LTV and credit score
const calculateCommercialInterestRate = (
  baseRate: number,
  creditScore: number,
  ltv: number,
  state?: RootState
) => {
  // If we have access to the Redux state, use the enhanced calculation
  if (state) {
    return calculateEnhancedInterestRate(
      state,
      'Conventional',
      baseRate,
      creditScore,
      ltv
    );
  }

  // Fallback to original calculation if no state provided
  let rateAdjustment = 0;

  // Credit score adjustments for Commercial loans (same as conventional)
  if (creditScore >= 760) {
    rateAdjustment -= 0.25;
  } else if (creditScore >= 740) {
    rateAdjustment -= 0.125;
  } else if (creditScore >= 720) {
    rateAdjustment -= 0.0625;
  } else if (creditScore >= 700) {
    // No change
  } else if (creditScore >= 680) {
    rateAdjustment += 0.125;
  } else if (creditScore >= 660) {
    rateAdjustment += 0.25;
  } else if (creditScore >= 640) {
    rateAdjustment += 0.375;
  } else if (creditScore >= 620) {
    rateAdjustment += 0.5;
  } else if (creditScore >= 600) {
    rateAdjustment += 0.625;
  } else if (creditScore >= 580) {
    rateAdjustment += 0.75;
  } else if (creditScore >= 560) {
    rateAdjustment += 0.875;
  } else if (creditScore >= 540) {
    rateAdjustment += 1.0;
  } else if (creditScore >= 520) {
    rateAdjustment += 1.125;
  } else {
    rateAdjustment += 1.25;
  }

  // LTV adjustments for Commercial loans
  if (ltv > 0.85) {
    rateAdjustment += 0.5;
  } else if (ltv > 0.8) {
    rateAdjustment += 0.375;
  } else if (ltv > 0.75) {
    rateAdjustment += 0.25;
  } else if (ltv > 0.7) {
    rateAdjustment += 0.125;
  } else {
    // LTV <= 70%, no additional adjustment
  }

  return Math.max(0, baseRate + rateAdjustment);
};

export const calculateCommercialClosingDisclosure = (
  {
    offerPrice,
    listPrice,
    downPayment = 0,
    zipCode = 0,
    hoaFees = 0,
    commission = 0,
    marketInterestRate = 6.5,
    creditScore = 0,
    rent = 0,
    loanTerm = 30,
    removePrepaymentPenalty = false,
  }: CalculateClosingDisclosureProps,
  state?: RootState
) => {
  const purchasePrice = offerPrice || listPrice;
  const loanAmount = purchasePrice - downPayment;
  const ltv = loanAmount / purchasePrice;

  const taxFactor = calculateTaxFactor(zipCode);

  // Enhanced interest rate calculation using the interest rate system
  const interestRate = state
    ? calculateEnhancedInterestRate(
        state,
        'Conventional',
        marketInterestRate,
        creditScore,
        ltv
      )
    : calculateCommercialInterestRate(
        marketInterestRate,
        creditScore,
        ltv,
        state
      );

  //change on user input on account page
  const numberOfPayments = loanTerm * 12;

  // constant fees
  const countyRecordingFees = 250;
  const stateRecordingFees = 150;
  const titleTitleSearchBase = 250;
  const recordingDeedBase = 75;
  const appraisalFeeBase = 2200;
  const underwritingFee = 995;
  const applicationFeeBase = 695;
  const rentalIncomeReportFee = 950;
  const recordingTitle = 0; // No separate title recording fee
  const recordingMortgage = 75;
  const surveyFee = 250;
  const pestInspectionFee = 145;
  const floodMonitoringFee = 25;
  const floodDeterminationFee = 35;
  const creditReportFeeBase = 75;
  const processingFeeBase = 395;
  const titleSettlementAgentFee = 650;
  const earnestMoneyDeposit = 1000;
  const insuranceBinder = 150;
  const lenderCredits = 0;
  const initialEscrowMortgageInsurance = 0;
  // Tax proration depends on the closing date; no flat credit is assumed.
  const prepaidsPropertyTaxes = 0;

  // factored calculations
  const annualPropertyTax = (purchasePrice * taxFactor) / 100;

  //UNFACTORED CALCULATIONS, NEED TO FIGURE OUT HOW TO INCLUDE CREDIT AND STATE FACTORS
  const optionalOwnersTitleInsuranceBase = purchasePrice * 0.00575; //State, Credit
  const minimumOwnersTitle = 1650;
  const maximumOwnersTitle = 5850;
  const optionalOwnersTitleInsurance = Math.min(
    Math.max(optionalOwnersTitleInsuranceBase, minimumOwnersTitle),
    maximumOwnersTitle
  );
  const recordingTransferTaxBase = purchasePrice * 0.01; //State dependent.
  const monthlyprivateMortgageInsuranceBase = (purchasePrice * 0.0075) / 12; //State, Credit
  const monthlyHomeownersInsuranceBase = (purchasePrice * 0.0103) / 12;
  const monthlyHomeownersInsurance =
    (purchasePrice * HOMEOWNERS_INSURANCE_RATE) / 12;
  const upfrontHomeownersInsurance = purchasePrice * HOMEOWNERS_INSURANCE_RATE;
  const monthlyPropertyTaxes = annualPropertyTax / 12;
  const prepaidsHomeownersInsurance =
    ((purchasePrice * HOMEOWNERS_INSURANCE_RATE) / 12) * 3;
  const titleLendersTitleInsuranceBase = purchasePrice * 0.0065;
  const upfrontMortgageInsurancePremiumBase = loanAmount * 0.0175;
  const homeInspectionFeeBase = purchasePrice * 0.0035;
  const minimumInspectionFee = 1650;
  const maximumInspectionFee = 8500;
  const homeInspectionFee = Math.min(
    Math.max(homeInspectionFeeBase, minimumInspectionFee),
    maximumInspectionFee
  );
  const initialEscrowTaxes = monthlyPropertyTaxes * 6; //
  const initialEscrowHOI = ((purchasePrice * 0.01) / 12) * 3;
  const estimatedEscrow = (purchasePrice * 0.0103) / 12 + monthlyPropertyTaxes;

  // Commercial-specific fees
  const commitmentFee = loanAmount * 0.005; // 0.5% of loan amount

  //these aren't going to change, they are determined
  const dailyInterestRate = interestRate / 365 / 100; // Convert annual rate to daily decimal
  const prepaidInterest = loanAmount * dailyInterestRate * 15; // Return the result rounded to 2 decimal places
  const monthlyInterestRate = interestRate / 12 / 100; // Convert annual rate to monthly decimal
  const principalAndInterest =
    (loanAmount *
      (monthlyInterestRate *
        Math.pow(1 + monthlyInterestRate, numberOfPayments))) /
    (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);
  const piti = principalAndInterest + estimatedEscrow;
  const transferTaxes = 0; // No transfer tax on refinance
  // Borrower's loan taxes still apply on a refinance (e.g. Florida doc
  // stamps + intangible tax) — see utils/fees/stateClosingTaxes.
  const stateTaxes = estimateStateClosingTaxes({
    zipCode,
    price: purchasePrice,
    loanAmount,
    transaction: 'refinance',
  });
  const mortgageTaxes = stateTaxes.mortgageTaxes;
  const cleanPayment = piti;
  // No owner's title policy on a refinance — the owner bought one at purchase.
  const titleCosts =
    insuranceBinder +
    titleTitleSearchBase +
    titleLendersTitleInsuranceBase +
    surveyFee +
    titleSettlementAgentFee;
  const prepaidCosts =
    initialEscrowTaxes +
    prepaidsPropertyTaxes +
    prepaidInterest +
    prepaidsHomeownersInsurance;
  const govCosts =
    stateRecordingFees +
    countyRecordingFees +
    recordingMortgage +
    recordingTitle +
    recordingDeedBase +
    transferTaxes +
    mortgageTaxes;
  const lenderCostTotals =
    floodDeterminationFee +
    floodMonitoringFee +
    creditReportFeeBase +
    appraisalFeeBase +
    applicationFeeBase +
    underwritingFee +
    processingFeeBase +
    rentalIncomeReportFee +
    commitmentFee;
  const closingCosts = govCosts + titleCosts + lenderCostTotals + prepaidCosts;
  const cashToClose = closingCosts + downPayment - earnestMoneyDeposit;
  const cashOutsideClosing =
    appraisalFeeBase + earnestMoneyDeposit + homeInspectionFee;
  const unbeatableCredit = (purchasePrice * commission) / 100; //this is where we put the buyers agent commission
  const unbeatableDeal = cashToClose - unbeatableCredit;

  // Perform calculations using the provided purchase price and constants
  // Prepare the closing disclosure object
  const closingDisclosure = {
    type: USER_LOAN_PROGRAM.COMMERCIAL,
    offerPrice,
    listPrice,
    purchasePrice,
    commission,
    marketInterestRate,
    creditScore,
    downPayment,
    loanAmount,
    interestRate,
    cleanPayment,
    cashOutsideClosing,
    processingFeeBase,
    principalAndInterest,
    homeInspectionFee,
    loanTerm,
    optionalOwnersTitleInsurance,
    hoaFees,
    earnestMoneyDeposit,
    piti,
    estimatedEscrow,
    monthlyPropertyTaxes,
    monthlyHomeownersInsurance,
    prepaidsHomeownersInsurance,
    prepaidsPropertyTaxes,
    prepaidInterest,
    titleSettlementAgentFee,
    titleLendersTitleInsuranceBase,
    titleTitleSearchBase,
    insuranceBinder,
    recordingDeedBase,
    appraisalFeeBase,
    underwritingFee,
    applicationFeeBase,
    upfrontHomeownersInsurance,
    pestInspectionFee,
    floodMonitoringFee,
    floodDeterminationFee,
    creditReportFeeBase,
    recordingTitle,
    recordingMortgage,
    countyRecordingFees,
    stateRecordingFees,
    transferTaxes,
    mortgageTaxes,
    stateTaxLines: stateTaxes.lines,
    stateTaxState: stateTaxes.state,
    initialEscrowTaxes,
    initialEscrowHOI,
    initialEscrowMortgageInsurance,
    lenderCredits,
    rentalIncomeReportFee,
    commitmentFee,
    cashToClose,
    unbeatableCredit,
    unbeatableDeal,
    ltv,
    numberOfPayments,
    dailyInterestRate,
    monthlyInterestRate,
    annualPropertyTax,
    taxFactor,
    monthlyprivateMortgageInsuranceBase,
    monthlyHomeownersInsuranceBase,
    upfrontMortgageInsurancePremiumBase,
    homeInspectionFeeBase,
    optionalOwnersTitleInsuranceBase,
    recordingTransferTaxBase,
    titleCosts,
    prepaidCosts,
    govCosts,
    lenderCostTotals,
    closingCosts,
    // Exactly what closingCosts sums — the fee breakdown itemizes these.
    closingCostParts: {
      stateRecordingFees,
      countyRecordingFees,
      recordingMortgage,
      recordingTitle,
      recordingDeedBase,
      transferTaxes,
      mortgageTaxes,
      insuranceBinder,
      titleTitleSearchBase,
      titleLendersTitleInsuranceBase,
      surveyFee,
      titleSettlementAgentFee,
      floodDeterminationFee,
      floodMonitoringFee,
      creditReportFeeBase,
      appraisalFeeBase,
      applicationFeeBase,
      underwritingFee,
      processingFeeBase,
      rentalIncomeReportFee,
      commitmentFee,
      initialEscrowTaxes,
      prepaidsPropertyTaxes,
      prepaidInterest,
      prepaidsHomeownersInsurance,
    },
    removePrepaymentPenalty,
  };

  return closingDisclosure;
};

export type ProductCommercialEsitmation = ReturnType<
  typeof calculateCommercialClosingDisclosure
>;
